import { useEffect } from 'react';
import { get } from '../api/commonAPI';

/**
 * Custom Hook: useMetaTags
 * Dynamically fetches and injects meta tags into the document head
 */
const useMetaTags = (type, identifier = null) => {
  useEffect(() => {
    const fetchAndInjectMetaTags = async () => {
      try {
        let path = 'ui/meta-tags/';
        
        if (type === 'blog' && identifier && identifier !== 'null' && identifier !== 'undefined') {
          path += `blog/${identifier}`;
        } else if (type === 'space' && identifier && identifier !== 'null' && identifier !== 'undefined') {
          path += `space/${identifier}`;
        } else {
          path += 'default';
        }

        const response = await get(path);
        
        if (response && response.status === 200 && response.data) {
          injectMetaTags(response.data);
        } else {
          // Fall back to default
          try {
            const defaultResponse = await get('ui/meta-tags/default');
            if (defaultResponse && defaultResponse.status === 200 && defaultResponse.data) {
              injectMetaTags(defaultResponse.data);
            }
          } catch (defaultError) {
            console.error('Failed to fetch default meta tags:', defaultError);
          }
        }
      } catch (error) {
        console.error('Error fetching meta tags:', error);
        
        // Fall back to default tags
        try {
          const defaultResponse = await get('ui/meta-tags/default');
          if (defaultResponse && defaultResponse.status === 200 && defaultResponse.data) {
            injectMetaTags(defaultResponse.data);
          }
        } catch (defaultError) {
          console.error('Error fetching default meta tags:', defaultError);
        }
      }
    };

    const injectMetaTags = (metaTags) => {
      if (!metaTags) return;

      const updateMetaTag = (name, content, property = false) => {
        let meta;
        
        if (property) {
          meta = document.querySelector(`meta[property="${name}"]`);
          
          if (!content) {
            if (meta) meta.remove();
            return;
          }
          
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('property', name);
            document.head.appendChild(meta);
          }
        } else {
          meta = document.querySelector(`meta[name="${name}"]`);
          
          if (!content) {
            if (meta) meta.remove();
            return;
          }
          
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', name);
            document.head.appendChild(meta);
          }
        }
        
        meta.setAttribute('content', content);
      };

      if (metaTags.title && !metaTags.title.startsWith('null')) {
        document.title = metaTags.title;
      }
      
      updateMetaTag('description', metaTags.description);
      updateMetaTag('keywords', metaTags.keywords);
      
      updateMetaTag('og:type', metaTags['og:type'], true);
      updateMetaTag('og:title', metaTags['og:title'], true);
      updateMetaTag('og:description', metaTags['og:description'], true);
      updateMetaTag('og:image', metaTags['og:image'], true);
      updateMetaTag('og:image:secure_url', metaTags['og:image:secure_url'], true);
      updateMetaTag('og:image:width', metaTags['og:image:width'], true);
      updateMetaTag('og:image:height', metaTags['og:image:height'], true);
      updateMetaTag('og:image:type', metaTags['og:image:type'], true);
      updateMetaTag('og:site_name', metaTags['og:site_name'], true);
      updateMetaTag('og:locale', metaTags['og:locale'], true);
      updateMetaTag('og:url', metaTags['og:url'], true);
      
      updateMetaTag('twitter:card', metaTags['twitter:card']);
      updateMetaTag('twitter:site', metaTags['twitter:site']);
      updateMetaTag('twitter:title', metaTags['twitter:title']);
      updateMetaTag('twitter:description', metaTags['twitter:description']);
      updateMetaTag('twitter:image', metaTags['twitter:image']);
      
      updateMetaTag('article:published_time', metaTags['article:published_time'], true);
      updateMetaTag('article:modified_time', metaTags['article:modified_time'], true);
      updateMetaTag('article:author', metaTags['article:author'], true);
    };
    
    fetchAndInjectMetaTags();
  }, [type, identifier]);
};

export default useMetaTags;
